import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  Page,
  Container,
  Section,
  Eyebrow,
  Display,
  SectionTitle,
  Lead,
  Card,
  Grid,
  Mono,
  PrimaryButton,
  GhostButton,
  TextLink,
  CtaBand,
  DISCORD_DM,
  DISCORD_SERVER,
} from '../Layout/PageLayout';
import { DiscordIcon } from '../Icons/CustomIcons';
import { useGames, formatCount, TITLE_COUNT } from '../Portfolio/games';

const HeroRow = styled(Container)`
  padding-top: 6rem;
  padding-bottom: 4.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 3.5rem;
  align-items: center;
`;

const Status = styled.div`
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1.75rem;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--live);
  }
`;

const Code = styled.figure`
  flex: 1 1 26rem;
  min-width: 0;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.625rem;
  overflow: hidden;
  font-family: var(--font-mono);

  > div,
  figcaption {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
    padding: 0.75rem 1rem;
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }

  > div {
    border-bottom: 1px solid var(--border);
  }

  figcaption {
    border-top: 1px solid var(--border);
    justify-content: flex-start;
    padding: 0.875rem 1.25rem;
  }

  pre {
    margin: 0;
    padding: 1.375rem 1.25rem;
    font-size: 0.84rem;
    line-height: 1.75;
    color: var(--text-code);
    overflow-x: auto;
  }

  .k { color: var(--brand); }
  .s { color: #9bd3b0; }
  .c { color: var(--text-tertiary); }
`;

const Stats = styled.div`
  border-top: 1px solid var(--border-subtle);
  border-bottom: 1px solid var(--border-subtle);
`;

const StatGrid = styled(Container)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13.75rem, 1fr));

  > div {
    padding: 2.25rem 1.5rem;
    border-right: 1px solid var(--border-subtle);
  }

  > div:last-child {
    border-right: 0;
  }

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 2.75rem;
    font-weight: 600;
    letter-spacing: -0.03em;
    line-height: 1;
  }

  span {
    display: block;
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-top: 0.625rem;
  }
`;

const HeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const Thumb = styled.div`
  aspect-ratio: 16 / 9;
  background: var(--surface-3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const WorkCard = styled(Card).attrs({ as: Link })`
  display: flex;
  flex-direction: column;
  color: var(--text-primary);
  transition: border-color var(--transition-base);

  &:hover {
    border-color: var(--border-accent);
  }

  h3 {
    font-size: 1.5rem;
    margin: 0.625rem 0 0.5rem;
  }
`;

const FeaturedCard = styled(WorkCard)`
  grid-column: 1 / -1;
  flex-direction: row;
  flex-wrap: wrap;

  ${Thumb} {
    flex: 1 1 35rem;
    min-width: 0;
  }

  h3 {
    font-size: 2.25rem;
    margin: 1rem 0 0.875rem;
  }
`;

const FeaturedBody = styled.div`
  flex: 1 1 21rem;
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1.25rem;
`;

const Metrics = styled.div`
  display: flex;
  gap: 2rem;
  border-top: 1px solid var(--border);
  padding-top: 1.25rem;

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 1.625rem;
    font-weight: 600;
  }

  span {
    font-size: 0.8125rem;
    color: var(--text-tertiary);
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(18.75rem, 1fr));
  border-top: 1px solid var(--border);

  > div {
    padding: 2.25rem 2rem 2.25rem 0;
  }

  > div + div {
    padding-left: 2rem;
    border-left: 1px solid var(--border);
  }

  @media (max-width: 1000px) {
    > div,
    > div + div {
      padding-left: 0;
      border-left: 0;
      border-bottom: 1px solid var(--border);
    }
  }

  h3 {
    font-size: 1.375rem;
    margin: 1.5rem 0 0.75rem;
  }

  p {
    margin-bottom: 1.25rem;
  }
`;

const Studios = styled(Container)`
  padding-top: 2.75rem;
  padding-bottom: 2.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem 4rem;

  strong {
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-code);
  }
`;

const Steps = styled.ol`
  flex: 1.4 1 32.5rem;
  min-width: 0;
  list-style: none;
  border-bottom: 1px solid var(--border);

  li {
    display: flex;
    gap: 1.75rem;
    padding: 1.75rem 0;
    border-top: 1px solid var(--border);
  }

  h3 {
    font-family: var(--font-sans);
    font-size: 1.125rem;
    letter-spacing: 0;
    margin-bottom: 0.375rem;
  }
`;

const Icon = ({ children }) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const CAPABILITIES = [
  {
    title: 'Backend architecture',
    text: 'Data flow and persistence are designed so that a game stays stable as it grows. Weekly updates can be added without rewriting the base systems.',
    tags: 'DataStores · session locking',
    icon: (
      <Icon>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <path d="M17.5 14v7M14 17.5h7" />
      </Icon>
    ),
  },
  {
    title: 'Performance under load',
    text: 'I profile the game first and then reduce replication and remote traffic. Bottlenecks are fixed before they affect live servers.',
    tags: 'MicroProfiler · parallel Luau',
    icon: (
      <Icon>
        <path d="M3 12h4l3-8 4 16 3-8h4" />
      </Icon>
    ),
  },
  {
    title: 'Team-ready gameplay code',
    text: 'Each system is a separate module with clear boundaries. Other scripters can extend a feature without breaking the released version.',
    tags: 'Rojo · Wally · Selene · code review',
    icon: (
      <Icon>
        <path d="M8 6l-6 6 6 6M16 6l6 6-6 6" />
      </Icon>
    ),
  },
];

const STEPS = [
  ['Send the scope', 'Describe the game and the current state of its code. Include your deadline.'],
  ['Get an answer', 'I usually reply within 24 hours with a proposed approach and a price.'],
  ['Milestone delivery', 'The work is delivered in parts that you can review, and each part comes with documentation.'],
  ['Live support', 'Ongoing work after release is optional, for example optimization or new content.'],
];

const FEATURED_ID = '9639553972';
const CARD_IDS = ['9468928549', '5837175781', '9344819762'];

const Home = () => {
  const games = useGames();
  const byId = (id) => games.find((g) => g.id === id);
  const featured = byId(FEATURED_ID);
  const totalVisits = games.reduce((sum, g) => sum + (g.visits || 0), 0);
  const visitsLabel = totalVisits ? `${Math.floor(totalVisits / 1e6)}M+` : '100M+';

  return (
    <Page>
      <HeroRow>
        <div style={{ flex: '1 1 32.5rem', minWidth: 0 }}>
          <Status>Available for new work · UTC+1</Status>
          <Display style={{ lineHeight: 1.02, fontSize: 'clamp(2.75rem, 6vw, 4.75rem)', marginBottom: '1.75rem' }}>
            Backend systems for Roblox games with live players.
          </Display>
          <Lead style={{ maxWidth: '33.75rem', marginBottom: '2.5rem' }}>
            My name is Skeered and I write Luau for Roblox games. I design data and networking systems, and I keep them
            fast when the player count grows.
          </Lead>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <PrimaryButton to="/portfolio">
              View selected work
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </PrimaryButton>
            <GhostButton to="/contact">Discuss a project</GhostButton>
          </div>
        </div>

        <Code>
          <div>
            <span>src/server/RemoteGuard.luau</span>
            <span>Luau · strict</span>
          </div>
          <pre>
            <span className="c">--!strict</span>
            {'\n'}<span className="k">const</span> RATE = <span className="s">8</span>  <span className="c">-- requests refilled per second</span>
            {'\n'}<span className="k">const</span> BURST = <span className="s">16</span> <span className="c">-- largest allowed burst</span>
            {'\n'}<span className="k">const</span> buckets: {'{'} [Player]: {'{'} tokens: number, at: number {'}'} {'}'} = {'{}'}
            {'\n\n'}<span className="k">local function</span> allow(player: Player): boolean
            {'\n  '}<span className="k">local</span> now = os.clock()
            {'\n  '}<span className="k">local</span> b = buckets[player] <span className="k">or</span> {'{'} tokens = BURST, at = now {'}'}
            {'\n  '}b.tokens = math.min(BURST, b.tokens + (now - b.at) * RATE)
            {'\n  '}b.at, buckets[player] = now, b
            {'\n  '}<span className="k">if</span> b.tokens &lt; <span className="s">1</span> <span className="k">then return false end</span>
            {'\n  '}b.tokens -= <span className="s">1</span>
            {'\n  '}<span className="k">return true</span>
            {'\n'}<span className="k">end</span>
          </pre>
          <figcaption>
            <span>
              <span style={{ color: 'var(--live)' }}>●</span> token bucket per player
            </span>
            <span>constant time per call</span>
          </figcaption>
        </Code>
      </HeroRow>

      <Stats aria-label="At a glance">
        <StatGrid>
          <div>
            <strong>{visitsLabel}</strong>
            <span>Total visits on games that include my code</span>
          </div>
          <div>
            <strong>4+ yrs</strong>
            <span>Working on Roblox games</span>
          </div>
          <div>
            <strong>{TITLE_COUNT}</strong>
            <span>Games I have worked on</span>
          </div>
          <div>
            <strong>3</strong>
            <span>Studios: Black Swan Hunters, Golden Games, Optidash</span>
          </div>
        </StatGrid>
      </Stats>

      <Section>
        <Container>
          <HeadRow>
            <div>
              <Eyebrow>01 / Selected work</Eyebrow>
              <SectionTitle style={{ margin: 0 }}>Live games with public player counts.</SectionTitle>
            </div>
            <TextLink to="/portfolio">All projects →</TextLink>
          </HeadRow>

          <Grid $min="21.25rem" $gap="1.5rem">
            <FeaturedCard to="/portfolio">
              <Thumb>{featured.image && <img src={featured.image} alt={`${featured.title} thumbnail`} />}</Thumb>
              <FeaturedBody>
                <div>
                  <Mono>BLACK SWAN HUNTERS · FULL GAME SCRIPTING</Mono>
                  <h3>{featured.title}</h3>
                  <p>
                    I scripted the full game. The work covers the core survival loop and the storage of player data.
                  </p>
                </div>
                <Metrics>
                  <div>
                    <strong>{formatCount(featured.visits)}</strong>
                    <span>visits</span>
                  </div>
                  <div>
                    <strong>{formatCount(featured.playing)}</strong>
                    <span>playing now</span>
                  </div>
                </Metrics>
              </FeaturedBody>
            </FeaturedCard>

            {CARD_IDS.map(byId).map((g) => (
              <WorkCard key={g.id} to="/portfolio">
                <Thumb>{g.image && <img src={g.image} alt={`${g.title} thumbnail`} />}</Thumb>
                <div style={{ padding: '1.625rem 1.75rem 1.75rem' }}>
                  <Mono>{`${g.studio} · ${g.role}`.toUpperCase()}</Mono>
                  <h3>{g.title}</h3>
                  <Mono $dim style={{ fontFamily: 'var(--font-sans)', fontSize: '0.875rem' }}>
                    {formatCount(g.visits)} visits
                  </Mono>
                </div>
              </WorkCard>
            ))}
          </Grid>
        </Container>
      </Section>

      <Section $tight>
        <Container>
          <Eyebrow>02 / What I do</Eyebrow>
          <SectionTitle style={{ marginBottom: '3.5rem' }}>Systems that run behind the gameplay.</SectionTitle>
          <Columns>
            {CAPABILITIES.map((c) => (
              <div key={c.title}>
                {c.icon}
                <h3>{c.title}</h3>
                <p>{c.text}</p>
                <Mono $dim>{c.tags}</Mono>
              </div>
            ))}
          </Columns>
        </Container>
      </Section>

      <Section $band style={{ padding: 0 }}>
        <Studios>
          <Mono $dim style={{ fontSize: '0.8125rem' }}>
            Studios
          </Mono>
          <strong>Black Swan Hunters</strong>
          <strong>Golden Games</strong>
          <strong>Optidash</strong>
        </Studios>
      </Section>

      <Section>
        <Container style={{ display: 'flex', flexWrap: 'wrap', gap: '3.5rem' }}>
          <div style={{ flex: '1 1 22.5rem', minWidth: 0 }}>
            <Eyebrow>03 / How a project works</Eyebrow>
            <SectionTitle style={{ marginBottom: '1.25rem' }}>Scope first. Then a realistic price.</SectionTitle>
            <p style={{ maxWidth: '26.25rem', marginBottom: '1.75rem' }}>
              You send the problem. I reply with an estimate of time and cost, and I also tell you whether the project
              suits my skills.
            </p>
            <TextLink to="/prices">See packages and pricing →</TextLink>
          </div>
          <Steps>
            {STEPS.map(([title, text], i) => (
              <li key={title}>
                <Mono $dim style={{ fontSize: '0.8125rem', paddingTop: 4 }}>
                  {String(i + 1).padStart(2, '0')}
                </Mono>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </Steps>
        </Container>
      </Section>

      <CtaBand
        title="Do you need an experienced Roblox developer?"
        text="Send a short description of your game and its timeline. Project requests are answered on Discord."
      >
        <PrimaryButton as="a" href={DISCORD_DM} target="_blank" rel="noopener noreferrer">
          <DiscordIcon size={18} />
          Message on Discord
        </PrimaryButton>
        <GhostButton as="a" href={DISCORD_SERVER} target="_blank" rel="noopener noreferrer">
          Join the server
        </GhostButton>
      </CtaBand>
    </Page>
  );
};

export default Home;
